import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";

import { Bullets, LegalDoc, LegalTable, Mail, Sub } from "./Doc";
import type { LegalSection } from "./Doc";

const EFFECTIVE = "September 12, 2026";
const UPDATED = "September 12, 2026";

const SECTIONS: readonly LegalSection[] = [
  {
    id: "who-we-are",
    title: "Who we are and what this covers",
    body: (
      <>
        <p>
          Chill is an AI website builder at getchill.ai. Customers update their
          website by texting, emailing, or chatting with an AI agent instead of
          using an editor. Chill is operated by Max Giraldo, a sole proprietor
          in Los Angeles, California, United States. "We", "us", and "our" mean
          Chill.
        </p>
        <p>
          This policy covers personal data we handle as a controller: data about
          people who visit getchill.ai, sign up for an account, message the
          agent, or pay us. It also explains, in section 10, the narrower role
          we play for data that belongs to a customer's own website visitors.
        </p>
        <p>
          It does not cover the websites our customers publish. Each customer
          decides what appears on their site and is responsible for it.
        </p>
      </>
    ),
  },
  {
    id: "data-we-collect",
    title: "Personal data we collect",
    body: (
      <>
        <p>
          We collect only what the Service needs in order to work. There is no
          tracking pixel, no advertising SDK, and no session recording anywhere
          on getchill.ai.
        </p>
        <LegalTable
          headers={["Category", "Examples", "Where it comes from"]}
          rows={[
            [
              "Account data",
              "Email address, name, business name, site slug, plan, account and organization identifiers",
              "You, at signup and in account settings; our authentication provider",
            ],
            [
              "Authentication data",
              "Sign-in events, magic-link or password authentication handled by AuthKit, session cookie identifiers",
              "You and WorkOS when you sign in",
            ],
            [
              "Contact channels",
              "Phone number if you bind one by text, the email addresses you send from",
              "You, when you opt in to a channel",
            ],
            [
              "Billing data",
              "Subscription tier, billing cycle, invoices, credit balance and ledger, card brand and last four digits, Stripe customer and payment identifiers",
              "You via Stripe Checkout; Stripe",
            ],
            [
              "Messages to the agent",
              "The text of your SMS, email, and web chat messages, attachments you send, the instruction the agent parsed, and the change it made",
              "You",
            ],
            [
              "Site content",
              "Everything you publish — events, photos, audio, hours, biography, contact details, and any personal data you choose to include",
              "You",
            ],
            [
              "Domain registration data",
              "Registrant name, postal address, phone, and email for any domain you register through us",
              "You, when you ask the agent to buy a domain",
            ],
            [
              "Vault credentials",
              "AWS access keys and GitHub tokens you choose to store so an agent can run commands on your Mac with them",
              "You, from the Chill app or a one-time link — never through the chat",
            ],
            [
              "Support data",
              "Emails you send us and our replies",
              "You",
            ],
            [
              "Operational logs",
              "IP address, user agent, request paths, timestamps, referrer, error traces",
              "Automatically, when you or a visitor uses the Service",
            ],
          ]}
        />
        <p>
          We do not collect special categories of data (health, biometrics,
          precise geolocation, political or religious views), we do not ask for
          government ID, and we never see your full card number, CVV, or bank
          details — Stripe collects those directly.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How we use personal data",
    body: (
      <>
        <Bullets
          items={[
            "Provide the Service: authenticate you, interpret your update requests, change and publish your site, register domains you ask for, and reply to you on the channel you used.",
            "Bill you: run subscriptions, meter credits, issue invoices and receipts, and handle failed payments, refunds, and disputes.",
            "Support you: answer questions, investigate a change the agent made, and restore or undo work.",
            "Keep the Service safe: detect abuse, spam, fraud, and attacks; enforce our Terms; and rate-limit or block bad traffic.",
            "Improve the Service: fix bugs and measure reliability using aggregate and operational data, not by reading your content for product research.",
            "Meet legal obligations: tax and accounting records, ICANN and registry requirements for domains, carrier and telecom compliance for SMS, and responses to lawful requests.",
          ]}
        />
        <p>
          We send transactional messages (confirmations of changes you asked
          for, billing notices, security notices, and material changes to these
          policies). We do not send marketing SMS. If we ever send a marketing
          email, it will carry a one-click unsubscribe link.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    title: "AI processing and model training",
    body: (
      <>
        <p>
          When you send a message, we pass it — together with the parts of your
          site the agent needs to act on and the tools available to it — to a
          large language model, which returns the agent's reply and the changes
          to apply.
        </p>
        <p>
          All model traffic goes through OpenRouter, an AI gateway that forwards
          each request to the model provider that serves the model we asked for.
          The agent that edits your site runs on Claude, made by Anthropic. The
          chat assistant on our homepage runs on Gemini, made by Google. Because
          a single model can be hosted by more than one provider, OpenRouter may
          route a request to an alternate host of the same model when the
          primary one is unavailable.
        </p>
        <Sub>What we do not do</Sub>
        <Bullets
          items={[
            "We do not use your messages, your site content, or your customers' data to train AI models — ours or anyone else's.",
            "We configure our gateway account to exclude model providers that train on the data sent to them, and we do not opt in to prompt logging in exchange for discounted inference.",
            "We do not sell or license your content to AI developers or data brokers.",
          ]}
        />
        <p>
          Model providers may retain a request briefly to run abuse detection
          and to meet their own legal obligations, under their published terms.
          We do not control those systems.
        </p>
        <p>
          Because we do not train on your data, there is no AI training opt-out
          to configure — the default is off for everyone.
        </p>
        <Sub>Credentials in your vault</Sub>
        <p>
          If you store an AWS key or GitHub token in your Chill vault, it is
          encrypted with a key held in AWS Key Management Service and stored
          separately from your site content. The agent can list what is stored
          and ask you to add or approve a credential, but no tool can return the
          value to it: when a command on your Mac needs the credential, our
          systems hand it directly to that one process and nothing is written
          to disk or into the conversation. Every use is recorded, you approve
          the first use on each Mac from your phone, and you can remove a
          credential at any time.
        </p>
        <p>
          Unlike a password manager, Chill can decrypt these credentials — that
          is what lets an agent use them when you are not at the keyboard. Store
          only keys scoped to what the agent needs, and rotate them if you leave.
        </p>
        <p>
          AI output can be wrong. The agent may misread a request and publish
          something you did not intend. Review the confirmations we send and ask
          the agent to undo or correct anything that looks off. See section 7 of
          the{" "}
          <Link
            to="/legal/$doc"
            params={{ doc: "terms" }}
            className="text-primary hover:underline"
          >
            Terms
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    id: "no-ads",
    title: "No advertising, no sale of personal data",
    body: (
      <>
        <p>
          We do not sell personal data, and we do not share it for cross-context
          behavioral advertising, as those terms are defined by California and
          other US state privacy laws. We run no ad platform integrations, no
          conversion pixels, and no data-broker relationships. We do not have a
          "Do Not Sell or Share My Personal Information" flow because there is
          nothing to opt out of.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and analytics",
    body: (
      <>
        <p>
          getchill.ai uses strictly necessary cookies only. There are no
          advertising cookies and no third-party analytics or session-recording
          scripts.
        </p>
        <LegalTable
          headers={["Cookie", "Purpose", "Lifetime"]}
          rows={[
            [
              "chill-session",
              "Keeps you signed in. An encrypted, HTTP-only session cookie.",
              "Until it expires or you sign out",
            ],
            [
              "chill-active-account",
              "Remembers which account you are working in when you have more than one.",
              "Until it expires or you sign out",
            ],
          ]}
        />
        <p>
          Sites published by our customers are served as static files and, by
          default, set no cookies. A customer can add third-party embeds to
          their own site; those embeds are governed by that customer's choices
          and the embed provider's policy, not ours.
        </p>
      </>
    ),
  },
  {
    id: "recipients",
    title: "Who receives your personal data",
    body: (
      <>
        <p>
          We share personal data with the service providers below, each bound by
          a contract to process it only on our instructions. This is our current
          subprocessor list.
        </p>
        <LegalTable
          headers={["Provider", "What it does", "Data it sees"]}
          rows={[
            [
              "Amazon Web Services (US)",
              "Hosting: compute, database, object storage, queues, CDN, and inbound/outbound email via SES",
              "All data we store, plus email content",
            ],
            [
              "OpenRouter (US)",
              "AI gateway: routes each model request to the provider serving that model",
              "Message content and the site context needed to act on it",
            ],
            [
              "Anthropic (US) and Google (US), via OpenRouter",
              "The models that interpret your requests — Claude for the site agent, Gemini for the homepage chat",
              "Message content and the site context needed to act on it",
            ],
            [
              "WorkOS (US)",
              "Hosted sign-in (AuthKit) and identity management",
              "Email address, name, authentication events",
            ],
            [
              "Stripe (US)",
              "Payments, subscriptions, and invoices",
              "Name, email, payment method, transaction history",
            ],
            [
              "Twilio (US)",
              "Sending and receiving SMS",
              "Phone number and message content",
            ],
            [
              "Porkbun (US)",
              "Domain registration and DNS for domains you buy through us",
              "Registrant contact details and domain names",
            ],
          ]}
        />
        <p>We may also disclose personal data:</p>
        <Bullets
          items={[
            "To comply with law, a subpoena, or a valid legal request — after reviewing it and, unless legally prohibited, telling you first.",
            "To protect the rights, safety, or property of Chill, our customers, or the public, including to investigate fraud or abuse.",
            "To a buyer or successor if the business is sold or reorganized. You will be notified before your data becomes subject to a different privacy policy.",
            "To professional advisers (accountants, lawyers) under a duty of confidence.",
          ]}
        />
        <p>
          If we add or replace a subprocessor, we update this list. Email{" "}
          <Mail /> to be notified when it changes.
        </p>
      </>
    ),
  },
  {
    id: "domains",
    title: "Domains you register through Chill",
    body: (
      <>
        <p>
          If you ask the agent to buy a domain, we collect the registrant
          contact details required by ICANN — name, postal address, phone
          number, and email address — and pass them to our registrar and to the
          registry operator for that top-level domain.
        </p>
        <Bullets
          items={[
            "Registration data is retained by the registrar and registry for as long as the domain exists and for a period afterwards set by ICANN and registry policy. We cannot delete it from those systems on request.",
            "We enable WHOIS privacy where the registry allows it, so your details are not published in public WHOIS. Some top-level domains and country-code registries require publication or disclosure regardless.",
            "Registries and registrars may be located outside the United States, depending on the top-level domain.",
            "Inaccurate registrant data can cause a domain to be suspended under ICANN rules, so give real details.",
          ]}
        />
      </>
    ),
  },
  {
    id: "shared-accounts",
    title: "Team members and shared accounts",
    body: (
      <>
        <p>
          An account can have more than one member. The account owner and other
          members can see the account's site content, the message history with
          the agent, credit usage, and billing status. If someone invites you to
          an account, assume what you send the agent there is visible to them.
        </p>
        <p>
          We do not claim organization-wide accounts based on your email domain,
          and signing up with a work address does not give your employer control
          over your account.
        </p>
      </>
    ),
  },
  {
    id: "visitors",
    title: "Your site's visitors",
    body: (
      <>
        <p>
          When we host a site for you, we handle limited data about the people
          who visit it — IP addresses and request logs, plus anything a visitor
          submits through a form or component you added (an RSVP, a contact
          message, a mailing-list signup).
        </p>
        <p>
          For that data you are the controller and we act as your processor: we
          handle it to run your site and on your instructions. You are
          responsible for telling your visitors what you collect and why, and
          for having a lawful basis to do it. Our{" "}
          <Link
            to="/legal/$doc"
            params={{ doc: "terms" }}
            className="text-primary hover:underline"
          >
            Terms
          </Link>{" "}
          and this policy together serve as our data processing terms; if you
          need a standalone data processing agreement, email <Mail />.
        </p>
      </>
    ),
  },
  {
    id: "sms",
    title: "SMS messaging",
    body: (
      <>
        <p>
          SMS is optional. You opt in by texting a binding code from your phone
          to our toll-free number; that inbound text is the opt-in. We then send
          transactional replies about the changes you request and answers to
          your questions. Message frequency varies — typically one reply per
          message you send. Message and data rates may apply.
        </p>
        <p>
          Reply STOP at any time to opt out, or HELP for help. We keep a record
          of each opt-in and opt-out (timestamp, phone number, message body, and
          the carrier message ID) so we can honor it and prove compliance. We do
          not send marketing, promotional, or third-party SMS, and we do not
          share phone numbers or SMS consent with anyone for their own marketing.
        </p>
        <p>
          Full program details are on the{" "}
          <Link to="/legal/sms" className="text-primary hover:underline">
            SMS program page
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    id: "legal-bases",
    title: "Legal bases for processing (EEA, UK, Switzerland)",
    body: (
      <>
        <LegalTable
          headers={["Purpose", "Legal basis"]}
          rows={[
            [
              "Creating your account, running the agent, publishing your site, registering domains",
              "Performance of a contract (GDPR Art. 6(1)(b))",
            ],
            [
              "Billing, invoicing, tax and accounting records",
              "Contract and legal obligation (Art. 6(1)(b), 6(1)(c))",
            ],
            [
              "Security, abuse prevention, debugging, and service reliability",
              "Legitimate interests in a safe, working service (Art. 6(1)(f))",
            ],
            [
              "Binding a phone number and sending SMS",
              "Consent, given by texting the binding code (Art. 6(1)(a))",
            ],
            [
              "Responding to legal requests and regulator obligations",
              "Legal obligation (Art. 6(1)(c))",
            ],
          ]}
        />
        <p>
          Where we rely on consent, you can withdraw it at any time without
          affecting processing already carried out. Where we rely on legitimate
          interests, you can object — see section 15.
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    title: "International data transfers",
    body: (
      <>
        <p>
          We store and process data in the United States. If you use the Service
          from outside the US, your data is transferred there and handled under
          US law, which may give you fewer protections than your home country.
        </p>
        <p>
          For transfers of EEA, UK, or Swiss personal data, we rely on the
          European Commission's Standard Contractual Clauses (and the UK
          Addendum or Swiss equivalent where applicable) with our providers, or
          on their certification under the EU–US Data Privacy Framework where
          they hold one. Ask us at <Mail /> for details of the safeguards in
          place.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "Data retention",
    body: (
      <>
        <p>
          We keep personal data for as long as your account is active, then for
          the periods below.
        </p>
        <LegalTable
          headers={["Data", "Retention"]}
          rows={[
            [
              "Account and site content",
              "While the account is active; 30 days after cancellation so you can export it, then deleted",
            ],
            [
              "Message history with the agent (SMS, email, chat)",
              "1 year, so you can audit and undo changes",
            ],
            [
              "Operational logs",
              "90 days",
            ],
            [
              "Billing and tax records",
              "7 years, as required by US tax law",
            ],
            [
              "SMS opt-in and opt-out records",
              "Kept indefinitely so we can continue to honor an opt-out",
            ],
            [
              "Domain registration data",
              "Held by the registrar and registry for the life of the domain and afterwards under ICANN and registry policy",
            ],
            [
              "Vault credentials",
              "Until you remove them or close your account; the use log is kept 1 year",
            ],
            [
              "Backups",
              "Overwritten on a rolling basis within 35 days of deletion",
            ],
          ]}
        />
        <p>
          We may keep data longer where a law, an open dispute, or a legal hold
          requires it, and will delete it once that ends.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "Security",
    body: (
      <>
        <Bullets
          items={[
            "Data is encrypted in transit with TLS and at rest by our hosting provider. Vault credentials are additionally envelope-encrypted with a per-credential key from AWS KMS, so a database copy alone cannot reveal them.",
            "Sign-in is handled by a dedicated identity provider; session cookies are encrypted and HTTP-only.",
            "Payment card data never touches our servers — Stripe collects and stores it.",
            "Access to production systems is limited to the operator and protected by two-factor authentication; access is logged.",
            "We use least-privilege cloud roles and keep secrets in a managed secrets store, not in code.",
          ]}
        />
        <p>
          No system is perfectly secure. If we confirm a breach affecting your
          personal data, we will notify you and any required regulator without
          undue delay and, where the law sets a deadline, within 72 hours of
          becoming aware of it. To report a vulnerability, email <Mail />.
        </p>
      </>
    ),
  },
  {
    id: "rights",
    title: "Your privacy rights and how to use them",
    body: (
      <>
        <p>Depending on where you live, you can ask us to:</p>
        <Bullets
          items={[
            "Access the personal data we hold about you and tell you how we use it.",
            "Correct data that is wrong or incomplete.",
            "Delete your data, subject to records we must keep (billing, opt-outs, legal holds).",
            "Export your data in a portable format.",
            "Restrict or object to processing based on legitimate interests.",
            "Withdraw consent, such as by replying STOP to SMS.",
            "Appeal a decision we make about one of these requests.",
          ]}
        />
        <p>
          Much of this is self-service: you can view, edit, export, and delete
          your content and close your account from your account settings. For
          anything else, email <Mail /> from the address on your account. We
          respond within 30 days (45 days for US state requests, extendable once
          where the law allows), and we will verify your identity before acting
          on a request — usually by confirming control of the account email.
        </p>
        <p>
          We will not discriminate against you for exercising a privacy right.
          You may use an authorized agent where the law permits; we will ask for
          proof of their authority.
        </p>
        <p>
          If you are in the EEA, UK, or Switzerland, you can also complain to
          your local data protection authority. We would rather hear from you
          first so we can fix it.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "Children",
    body: (
      <p>
        The Service is for businesses and is not directed at children. You must
        be at least 16 to create an account. We do not knowingly collect
        personal data from children under 16, and we do not sell or share the
        personal data of anyone under 16. If you believe a child has given us
        personal data, email <Mail /> and we will delete it.
      </p>
    ),
  },
  {
    id: "us-states",
    title: "United States state privacy disclosures",
    body: (
      <>
        <p>
          This section applies to residents of California, Colorado,
          Connecticut, Virginia, Texas, and other states with comprehensive
          privacy laws. In the last 12 months we collected the categories below
          and disclosed them only to the service providers listed in section 7.
        </p>
        <LegalTable
          headers={[
            "Category (CCPA)",
            "Collected",
            "Sold or shared",
            "Disclosed to",
          ]}
          rows={[
            [
              "Identifiers (name, email, phone, IP address, account IDs)",
              "Yes",
              "No",
              "Hosting, identity, payments, SMS, domain providers",
            ],
            [
              "Customer records (billing address, payment method details)",
              "Yes",
              "No",
              "Payment processor",
            ],
            [
              "Commercial information (subscription, credits, purchases)",
              "Yes",
              "No",
              "Hosting, payment processor",
            ],
            [
              "Internet activity (request logs, pages viewed on getchill.ai)",
              "Yes",
              "No",
              "Hosting provider",
            ],
            [
              "Content you submit (messages, site content, attachments)",
              "Yes",
              "No",
              "Hosting provider, AI gateway and model providers",
            ],
            [
              "Sensitive personal information",
              "No",
              "No",
              "—",
            ],
            [
              "Biometric, geolocation, education, or employment data",
              "No",
              "No",
              "—",
            ],
          ]}
        />
        <p>
          We do not sell personal information, do not share it for
          cross-context behavioral advertising, and do not use or disclose
          sensitive personal information for purposes requiring a right to
          limit. We do not profile people in a way that produces legal or
          similarly significant effects, and we do not need to process opt-out
          preference signals because we run no advertising trackers.
        </p>
        <p>
          To exercise a right — know, access, correct, delete, or port — email{" "}
          <Mail />. If we deny a request, you may appeal by replying to our
          decision; we will respond to the appeal within 45 days and, if we
          still deny it, tell you how to contact your state attorney general.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <p>
        We may update this policy as the Service changes. If a change materially
        affects how we use your personal data, we will email account holders at
        least 14 days before it takes effect. Other changes take effect when
        posted. The effective and last-updated dates at the top of this page
        always tell you which version is current.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact us",
    body: (
      <>
        <p>
          Questions, requests, or complaints about privacy: <Mail />. We read
          every one.
        </p>
        <p>
          Chill
          <br />
          Max Giraldo, sole proprietor
          <br />
          Los Angeles, California, United States
        </p>
        <p>
          We do not have an EU or UK representative under GDPR Article 27. If
          you are in the EEA or UK and need one, contact us and we will tell you
          how to proceed.
        </p>
      </>
    ),
  },
];

export function Privacy(): ReactElement {
  return (
    <LegalDoc
      doc="privacy"
      title="Privacy Policy"
      effective={EFFECTIVE}
      updated={UPDATED}
      summary={
        <>
          <p>
            The short version: we collect what it takes to run your website and
            bill you for it. We do not sell your data, we do not run ads or
            trackers, and we do not train AI models on your content or your
            messages. You can export or delete everything at any time.
          </p>
          <p className="mt-4">
            The long version is below, and it is the one that governs.
          </p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
